import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatTRY, TR_MONTHS } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { ChevronLeft, ChevronRight, Fuel, CreditCard, Landmark } from "lucide-react";

const typeMeta = {
  fuel: { color: "#F59E0B", icon: Fuel, label: "Yakıt" },
  card: { color: "#3B82F6", icon: CreditCard, label: "Kart" },
  loan: { color: "#8B5CF6", icon: Landmark, label: "Kredi" },
};
const filters = [
  { key: "all", label: "Tümü" },
  { key: "fuel", label: "Yakıt" },
  { key: "card", label: "Kartlar" },
  { key: "loan", label: "Krediler" },
];
const WEEK = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

export default function CalendarPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get(`/calendar?year=${year}&month=${month + 1}`).then((r) => setEvents(r.data.events)).catch(() => {});
  }, [year, month]);

  const shown = events.filter((e) => filter === "all" || e.type === filter);
  const monthTotal = shown.reduce((s, e) => s + e.amount, 0);

  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday start
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const byDay = {};
  shown.forEach((e) => {
    const d = new Date(e.date).getDate();
    (byDay[d] = byDay[d] || []).push(e);
  });

  const prev = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); setSelected(null); };
  const next = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); setSelected(null); };

  const todayD = now.getFullYear() === year && now.getMonth() === month ? now.getDate() : -1;
  const cells = [...Array(startOffset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const selectedEvents = selected ? byDay[selected] || [] : [];

  return (
    <div className="p-5 md:p-8 max-w-6xl">
      <PageHeader
        testid="calendar-header"
        title="Birleşik Ödeme Takvimi"
        subtitle="Yakıt (turuncu), kredi kartı (mavi) ve banka kredisi (mor) ödemeleri tek takvimde."
      />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <button onClick={prev} data-testid="calendar-prev" className="p-2 rounded-lg bg-[#161D2A] hover:bg-[#1D2637] border border-[#253043] transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <div className="font-display text-lg font-bold w-40 text-center">{TR_MONTHS[month]} {year}</div>
          <button onClick={next} data-testid="calendar-next" className="p-2 rounded-lg bg-[#161D2A] hover:bg-[#1D2637] border border-[#253043] transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-2">
          {filters.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`calendar-filter-${f.key}`}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${filter === f.key ? "bg-[#E65100] border-[#E65100] text-white" : "bg-[#161D2A] border-[#253043] text-[#94A3B8]"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card-surface p-4 mb-5 flex items-center justify-between fade-up">
        <span className="text-sm text-[#94A3B8]">{TR_MONTHS[month]} ayı toplam nakit ihtiyacı</span>
        <span className="font-mono-num text-xl font-bold" data-testid="calendar-month-total">{formatTRY(monthTotal)}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card-surface p-4 lg:col-span-2 fade-up">
          <div className="grid grid-cols-7 gap-1.5 mb-2">
            {WEEK.map((w) => <div key={w} className="text-center text-[11px] text-[#94A3B8] py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {cells.map((day, i) => {
              if (day === null) return <div key={`e${i}`} />;
              const evs = byDay[day] || [];
              const dayTotal = evs.reduce((s, e) => s + e.amount, 0);
              const hasOverdue = evs.some((e) => e.overdue);
              const isToday = day === todayD;
              const isSel = day === selected;
              return (
                <button
                  key={day} data-testid="calendar-day-cell"
                  onClick={() => setSelected(isSel ? null : day)}
                  className={`min-h-[62px] md:min-h-[80px] p-1.5 rounded-lg text-left transition-colors border ${isSel ? "border-[#E65100] bg-[#1D2637]" : hasOverdue ? "border-[#EF4444]/60 bg-[#EF4444]/[0.06] hover:bg-[#1D2637]" : evs.length ? "border-[#253043] bg-[#111620] hover:bg-[#1D2637]" : "border-transparent hover:bg-[#111620]"}`}
                >
                  <div className={`text-xs font-medium mb-1 inline-flex w-5 h-5 items-center justify-center rounded-full ${isToday ? "bg-[#E65100] text-white" : "text-[#94A3B8]"}`}>{day}</div>
                  <div className="flex gap-0.5 flex-wrap">
                    {evs.slice(0, 4).map((e, j) => (
                      <span key={j} className="w-1.5 h-1.5 rounded-full" style={{ background: e.overdue ? "#EF4444" : typeMeta[e.type].color }} />
                    ))}
                  </div>
                  {dayTotal > 0 && <div className={`text-[9px] md:text-[10px] font-mono-num mt-1 truncate ${hasOverdue ? "text-[#EF4444]" : "text-[#94A3B8]"}`}>{(dayTotal / 1000).toFixed(0)}k ₺</div>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="card-surface p-5 fade-up">
          <h3 className="font-display font-semibold mb-4">
            {selected ? `${selected} ${TR_MONTHS[month]}` : "Tüm Ödemeler"}
          </h3>
          <div className="space-y-3 max-h-[440px] overflow-y-auto">
            {(selected ? selectedEvents : shown).length === 0 && (
              <div className="text-sm text-[#94A3B8] py-6 text-center">Ödeme yok.</div>
            )}
            {(selected ? selectedEvents : shown).map((e, i) => {
              const m = typeMeta[e.type];
              return (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl bg-[#111620] ${e.overdue ? "border border-[#EF4444]/40" : ""}`} data-testid="calendar-event-item">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.color}1a` }}>
                    <m.icon className="w-4 h-4" style={{ color: m.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{e.title}</div>
                    {e.overdue ? (
                      <div className="text-xs text-[#EF4444]">{new Date(e.date).getDate()} {TR_MONTHS[month]} — Vadesi geçti</div>
                    ) : (
                      !selected && <div className="text-xs text-[#94A3B8]">{new Date(e.date).getDate()} {TR_MONTHS[month]}</div>
                    )}
                  </div>
                  <div className={`font-mono-num text-sm font-semibold shrink-0 ${e.overdue ? "text-[#EF4444]" : ""}`}>{formatTRY(e.amount)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
